import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal, scrollToId } from "@/components/Reveal";

export const TruffleSpotlight = () => {
    const { t } = useLanguage();
    const lines = t("spotlight.lines");
    const points = t("spotlight.points");

    return (
        <section data-testid="spotlight-section" className="relative overflow-hidden border-y border-white/10 bg-[#22151b]">
            <div className="pointer-events-none absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-midora-red/15 blur-[140px]" />
            <div className="mx-auto grid max-w-[1600px] gap-14 px-5 py-24 md:px-12 md:py-32 lg:grid-cols-2 lg:gap-20 lg:px-24 lg:py-36">
                <div>
                    <Reveal>
                        <span
                            data-testid="spotlight-badge"
                            className="inline-flex items-center gap-2 border border-[#DBD1BD]/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#DBD1BD]"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-midora-red opacity-60" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-midora-red" />
                            </span>
                            {t("spotlight.badge")}
                        </span>
                        <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">{t("spotlight.overline")}</p>
                        <h2 className="mt-4 font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl" data-testid="spotlight-title">
                            {lines[0]}
                            <br />
                            <em className="italic text-midora-red">{lines[1]}</em>
                        </h2>
                        <p className="mt-8 max-w-lg text-sm leading-relaxed text-white/60 md:text-base" data-testid="spotlight-text">
                            {t("spotlight.text")}
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <ul className="mt-10 space-y-4">
                            {points.map((p) => (
                                <li key={p} className="flex items-center gap-3 text-sm text-white/75">
                                    <Check className="h-4 w-4 shrink-0 text-midora-red" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                        <button
                            data-testid="spotlight-cta"
                            onClick={() => scrollToId("contatti")}
                            className="mt-10 bg-midora-red px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#DBD1BD] hover:text-[#1C2029]"
                        >
                            {t("spotlight.cta")}
                        </button>
                        <div>
                            <Link
                                to="/tartufi"
                                data-testid="spotlight-more-link"
                                className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#DBD1BD]/70 transition-colors duration-300 hover:text-white"
                            >
                                {t("spotlight.more")} <ArrowRight className="h-4 w-4 text-midora-red" />
                            </Link>
                        </div>
                    </Reveal>
                </div>

                <div className="relative flex items-center">
                    <Reveal delay={0.15} className="relative w-full">
                        <motion.div
                            whileHover={{ rotate: 0, scale: 1.02 }}
                            initial={{ rotate: -2 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            className="relative z-10 ml-auto w-[78%] overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(200,16,46,0.15)]"
                        >
                            <img src="/assets/hero-truffle.jpg" alt="Tartufo bianco" className="aspect-[4/3] w-full object-cover" data-testid="spotlight-image-truffle" />
                        </motion.div>
                        <motion.div
                            whileHover={{ rotate: 0, scale: 1.02 }}
                            initial={{ rotate: 3 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute -bottom-16 left-0 z-20 w-[52%] overflow-hidden border border-white/10"
                        >
                            <img src="/assets/porcini.jpg" alt="Funghi porcini" className="aspect-[4/5] w-full object-cover" data-testid="spotlight-image-porcini" />
                        </motion.div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
